
import { BarsArrowUpIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/solid';
import type { CustomFlowbiteTheme, TabsRef } from 'flowbite-react';
import { Tabs } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { InteractionFormData, LeadFormData, SalesManagerInteraction } from '../../../@types/contactInteraction';
import type { Owner } from '../../../@types/Owner';
import { OWNER_STEPS } from '../../../@types/Owner';
import type { RootState } from '../../../store/store';
import Badge from '../../common/Badge';
import Spinner from '../../common/Spinner';
import WarningValidationPending from '../../common/WarningValidationPending';
import { CoproAffectationToggle, getStoredPreference } from '../components/CoproAffectationToggle';
import DueFilterChips from '../components/DueFilterChips';
import PendingTable from '../components/PendingTable';
import { fetchOwnersByStep } from '../OwnerCommunication/utils/ownerStepApi';
import type { DueFilter, MeetingTabId } from '../utils/crmDeepLinks';
import { getDueFilters, parseCrmTab, parseDueFilter } from '../utils/crmDeepLinks';
import { ClosedFollowUpTable, RefusedTable, ToContactTable } from './components';
import LeadForm from './components/LeadForm';

interface TabData {
  status: AsyncStatus;
  data: Owner[] | SalesManagerInteraction[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalResults: number;
  };
  sortField?: string;
}

// Custom theme for tabs
const customTheme: CustomFlowbiteTheme['tabs'] = {
  base: 'flex flex-col',
  tablist: {
    base: 'flex text-center',
    tabitem: {
      base: 'flex items-center justify-center rounded-t-lg p-4 text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 w-full md:w-auto',
      variant: {
        default: {
          base: 'text-gray-500 dark:text-gray-400',
          active: {
            on: 'active bg-gray-100 p-4 text-rgpd-blue',
            off: 'bg-white hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white',
          },
        },
      },
    },
  },
};

// Define the tabs and their corresponding steps
const TABS: Array<{ id: MeetingTabId; title: string; step: string; icon: typeof ClockIcon }> = [
  { 
    id: 'a-contacter', 
    title: 'A contacter', 
    step: OWNER_STEPS.TO_CONTACT_SALES_MANAGER, 
    icon: BarsArrowUpIcon, 
  },
  { 
    id: 'en-attente', 
    title: 'En attente de rendez-vous', 
    // Include both standard and incoming-call specific steps
    step: [OWNER_STEPS.WAITING_FOR_MEETING_SALES_MANAGER, OWNER_STEPS.WAITING_FOR_MEETING_FROM_INCOMING_CALL_SALES_MANAGER].join(','),
    icon: ClockIcon, 
  },
  { 
    id: 'refus', 
    title: 'Refus', 
    step: OWNER_STEPS.REFUSED_SALES_MANAGER, 
    icon: XCircleIcon, 
  },
  { 
    id: 'suivi-cloture', 
    title: 'Suivi cloturé', 
    step: OWNER_STEPS.CLOSED_SALES_MANAGER, 
    icon: CheckCircleIcon, 
  },
];

const TAB_IDS = TABS.map(tab => tab.id);

function SalesMeeting() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const location = useLocation();
  // Deep link from the dashboard: ?tab=<tab id>&due=late|today|tomorrow (due filters the given tab)
  const initialTabId = parseCrmTab(location.search, TAB_IDS) ?? 'a-contacter';
  const initialDue = parseDueFilter(location.search);
  const [tabsData, setTabsData] = useState<{
    [key: string]: TabData
  }>({
    'a-contacter': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'en-attente': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'refus': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'suivi-cloture': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 }, sortField: '' },
  });
  const tabsRef = useRef<TabsRef>(null);
  const assignedCoprosOnlyRef = useRef(getStoredPreference());

  // « En attente de rendez-vous »: date of the meeting with the manager
  const [meetingFilter, setMeetingFilter] = useState<DueFilter>(initialTabId === 'en-attente' ? initialDue : 'all');
  // « A contacter »: date of the meeting booked by the référent qualité
  const [qualityMeetingFilter, setQualityMeetingFilter] = useState<DueFilter>(initialTabId === 'a-contacter' ? initialDue : 'all');

  const getAffectationFilters = (): Record<string, string> =>
    assignedCoprosOnlyRef.current ? { filterAssignedCoprosOnly: '1' } : {};

  const getMeetingFilters = (filter: DueFilter): Record<string, string> => getDueFilters('filterSalesManagerInteractionMeetingAt', filter);

  const getTabFilters = (tabId: string, filters: { meeting?: DueFilter; qualityMeeting?: DueFilter } = {}): Record<string, string> => ({
    ...getAffectationFilters(),
    ...(tabId === 'en-attente' ? getMeetingFilters(filters.meeting ?? meetingFilter) : {}),
    ...(tabId === 'a-contacter' ? getDueFilters('filterQualityReferenceInteractionMeetingAt', filters.qualityMeeting ?? qualityMeetingFilter) : {}),
  });

  const fetchTabData = async(tabId: string, step: string, page: number = 0, limit: number = 10, sortField?: string, extraFilters?: Record<string, string>) => {
    if (!company || !jwt || !company.id) {return;}

    setTabsData(prev => ({
      ...prev,
      [tabId]: { 
        ...prev[tabId], 
        status: 'PENDING',
        sortField: sortField || prev[tabId].sortField, 
      },
    }));

    try {
      const response = await fetchOwnersByStep(
        company.id, 
        step, 
        jwt, 
        page + 1, 
        limit,
        sortField,
        extraFilters,
      );
      
      setTabsData(prev => ({
        ...prev,
        [tabId]: { 
          status: 'SUCCESS', 
          data: response.results,
          pagination: {
            currentPage: page,
            itemsPerPage: limit,
            totalResults: response.pagination.totalResults,
          },
          sortField,
        },
      }));
    } catch (error) {
      console.error(`Error fetching data for tab ${tabId}:`, error);
      setTabsData(prev => ({
        ...prev,
        [tabId]: { ...prev[tabId], status: 'ERROR' },
      }));
    }
  };

  const handleMeetingFilterChange = (filter: DueFilter) => {
    setMeetingFilter(filter);
    const tab = TABS.find(t => t.id === 'en-attente');
    if (tab) {
      fetchTabData('en-attente', tab.step, 0, tabsData['en-attente'].pagination.itemsPerPage, tabsData['en-attente'].sortField, getTabFilters('en-attente', { meeting: filter }));
    }
  };

  const handleQualityMeetingFilterChange = (filter: DueFilter) => {
    setQualityMeetingFilter(filter);
    const tab = TABS.find(t => t.id === 'a-contacter');
    if (tab) {
      fetchTabData('a-contacter', tab.step, 0, tabsData['a-contacter'].pagination.itemsPerPage, tabsData['a-contacter'].sortField, getTabFilters('a-contacter', { qualityMeeting: filter }));
    }
  };

  // Handle page change for a specific tab
  const handlePageChange = (tabId: string, step: string) => (page: number, rowsPerPage: number) => {
    const extra = getTabFilters(tabId);
    const currentRowsPerPage = tabsData[tabId].pagination.itemsPerPage;
    if (currentRowsPerPage !== rowsPerPage) {
      fetchTabData(tabId, step, 0, rowsPerPage, tabsData[tabId].sortField, extra);
    } else {
      fetchTabData(tabId, step, page, rowsPerPage, tabsData[tabId].sortField, extra);
    }
  };

  // Handle sort change for a specific tab
  const handleSortChange = (tabId: string, step: string) => (sortField: string) => {
    const extra = getTabFilters(tabId);
    fetchTabData(tabId, step, 0, tabsData[tabId].pagination.itemsPerPage, sortField, extra);
  };

  // Fetch initial data for all tabs
  const fetchAllData = async() => {
    if (!company || !jwt || !company.id) {return;}

    for (const tab of TABS) {
      const extra = getTabFilters(tab.id);
      fetchTabData(tab.id, tab.step, 0, 10, undefined, extra);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [company, jwt]);

  // Handle interaction form submission
  const handleInteractionSubmit = (_ownerId: number, _formData: InteractionFormData) => {
    // In a real app, this would be an API call to create a new interaction
    // Refresh data after submission
    console.warn('Submitting interaction for owner', _ownerId, _formData);
    fetchAllData();
  };

  // Handle lead form submission
  const handleLeadSubmit = (_interactionId: number, _formData: LeadFormData) => {
    // In a real app, this would be an API call to create a new lead
    console.warn('Submitting lead for interaction', _interactionId, _formData);
    fetchAllData();
  };

  const handleCoproFilterChange = (assignedCoprosOnly: boolean) => {
    assignedCoprosOnlyRef.current = assignedCoprosOnly;
    fetchAllData();
  };

  // Fetch data for specific tabs
  const fetchSpecificTabs = async(tabIds: string[]) => {
    if (!company || !jwt || !company.id) {return;}

    for (const tabId of tabIds) {
      const tab = TABS.find(t => t.id === tabId);
      if (tab) {
        const currentPage = tabsData[tabId].pagination.currentPage;
        const itemsPerPage = tabsData[tabId].pagination.itemsPerPage;
        const sortField = tabsData[tabId].sortField;
        const extra = getTabFilters(tabId);
        fetchTabData(tab.id, tab.step, currentPage, itemsPerPage, sortField, extra);
      }
    }
  };

  return (
    <div className="mb-8 overflow-x-scroll rounded-lg bg-white p-4 shadow-md">
      <div className="mb-6">
        <p className="mb-4 text-gray-600">
          Gérez efficacement vos rendez-vous avec les propriétaires à travers les différentes étapes du processus.
        </p>
      </div>

      <WarningValidationPending />

      <div className="mb-4">
        <CoproAffectationToggle onFilterChange={handleCoproFilterChange} />
      </div>

      <div className="rounded-lg bg-white shadow">
        <Tabs theme={customTheme} ref={tabsRef}>
          {TABS.map((tab) => (
            <Tabs.Item 
              key={tab.id}
              title={`${tab.title} ${tabsData[tab.id].pagination ? `(${tabsData[tab.id].pagination.totalResults})` : ''}`} 
              icon={tab.icon}
              active={tab.id === initialTabId}
            >
              {tab.id === 'a-contacter' && (
                <div className="px-4 pt-4">
                  <DueFilterChips label="RDV qualité" value={qualityMeetingFilter} onChange={handleQualityMeetingFilterChange} />
                </div>
              )}
              {tab.id === 'en-attente' && (
                <div className="px-4 pt-4">
                  <DueFilterChips label="Rendez-vous" value={meetingFilter} onChange={handleMeetingFilterChange} />
                </div>
              )}
              {tabsData[tab.id].status === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {tabsData[tab.id].status === 'ERROR' && (
                <div className="w-full text-center">
                  <Badge
                    mode="error"
                    text="Une erreur est survenue au chargement de vos données"
                    className="mx-10 my-32"
                  />
                </div>
              )}
              {tabsData[tab.id].status === 'SUCCESS' && tabsData[tab.id].data && tabsData[tab.id].data.length > 0 && (
                <div className="p-4">
                  {tab.id === 'a-contacter' && (
                    <ToContactTable 
                      data={tabsData[tab.id].data as Owner[]} 
                      onInteractionSubmit={handleInteractionSubmit}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onRefresh={() => fetchSpecificTabs(['a-contacter', 'en-attente'])}
                    />
                  )}
                  {tab.id === 'en-attente' && (
                    <PendingTable
                      data={tabsData[tab.id].data as Owner[]}
                      interactionKey="salesManagerInteractions"
                      showMeetingColumn={true}
                      showContacts={false}
                      apiPagination={true}
                      LeadForm={LeadForm}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onLeadSubmit={handleLeadSubmit}
                      onRefresh={() => fetchSpecificTabs(['en-attente', 'suivi-cloture'])}
                    />
                  )}
                  {tab.id === 'refus' && (
                    <RefusedTable
                      data={tabsData[tab.id].data as Owner[]}
                      interactionKey="salesManagerInteractions"
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                    />
                  )}
                  {tab.id === 'suivi-cloture' && (
                    <ClosedFollowUpTable
                      interactionKey="salesManagerInteractions"
                      data={tabsData[tab.id].data as Owner[]}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onSortChange={handleSortChange(tab.id, tab.step)}
                      currentSortField={tabsData[tab.id].sortField}
                    />
                  )}
                </div>
              )}
              {tabsData[tab.id].status === 'SUCCESS' && tabsData[tab.id].data && tabsData[tab.id].data.length === 0 && (
                <div className="p-4">
                  <div className="w-full text-center">
                    <Badge mode="warning" text={`Aucun propriétaire "${tab.title.toLowerCase()}" trouvé`} className="mx-10 my-32" />
                  </div>
                </div>
              )}
            </Tabs.Item>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

export default SalesMeeting; 